import { useEffect, useState, type FormEvent } from 'react'
import { Copy, Loader2, Shield, Trash2 } from 'lucide-react'
import { Modal, Field } from '@/shared/ui/Modal'
import { Note } from '@/shared/ui/states'
import { ApiError, explain } from '@/shared/api/problem'
import { cn } from '@/lib/utils'
import { cloneRole, createRole, deleteRole, updateRole } from './api'
import type { RoleResponse } from '@/shared/api/types'

/** PollSphere's own chocolate plus a spread that stays legible on both themes. */
const COLOURS = [
  '#dc2626', '#ea580c', '#d97706', '#65a30d', '#059669',
  '#0891b2', '#2563eb', '#7c3aed', '#c026d3', '#64748b',
]

function ColourPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {COLOURS.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          aria-label={`Colour ${c}`}
          className={cn(
            'h-7 w-7 rounded-md border-2 transition-transform',
            value === c ? 'border-foreground scale-110' : 'border-transparent hover:scale-105',
          )}
          style={{ background: c }}
        />
      ))}
    </div>
  )
}

/**
 * Create a role.
 *
 * `name` is the stable machine identifier and is immutable after creation — the
 * backend refuses to change it on PATCH — so it is worth getting right, and the
 * field says so rather than letting someone discover it later.
 */
export function CreateRoleDialog({
  open, onClose, onCreated,
}: { open: boolean; onClose: () => void; onCreated: (role: RoleResponse) => void }) {
  const [name, setName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [description, setDescription] = useState('')
  const [colour, setColour] = useState(COLOURS[6] as string)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  function reset() {
    setName(''); setDisplayName(''); setDescription(''); setColour(COLOURS[6] as string)
    setError(null); setFieldErrors({})
  }

  // Derive the machine name from the display name until it is edited directly.
  const [nameTouched, setNameTouched] = useState(false)
  useEffect(() => {
    if (nameTouched) return
    setName(displayName.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, ''))
  }, [displayName, nameTouched])

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null); setFieldErrors({})
    try {
      const role = await createRole({
        name: name.trim(),
        displayName: displayName.trim(),
        description: description.trim() || undefined,
        colour,
      })
      reset()
      onCreated(role)
      onClose()
    } catch (err) {
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors())
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose() }}
      title="Create role"
      description="A role is a named group of permissions. Users get permissions only through roles."
      footer={
        <>
          <button type="button" className="erp-btn" onClick={() => { reset(); onClose() }}>Cancel</button>
          <button type="submit" form="create-role-form" className="erp-btn erp-btn-primary"
                  disabled={busy || !name.trim() || !displayName.trim()}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Shield className="h-3.5 w-3.5" />}
            Create role
          </button>
        </>
      }
    >
      <form id="create-role-form" onSubmit={submit} className="space-y-4">
        {error && <Note tone="bad">{error}</Note>}
        <Field label="Display name" required error={fieldErrors.displayName}
               hint="What people see. e.g. “County Coordinator”">
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                 className="erp-input w-full" maxLength={160} required />
        </Field>
        <Field label="Name" required error={fieldErrors.name}
               hint="Machine identifier — unique and permanent. It cannot be changed later.">
          <input
            value={name}
            onChange={(e) => { setNameTouched(true); setName(e.target.value.toUpperCase()) }}
            className="erp-input w-full font-mono"
            maxLength={64}
            pattern="[A-Z0-9_]+"
            required
          />
        </Field>
        <Field label="Description" error={fieldErrors.description}>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                    rows={2} className="erp-input w-full !h-auto py-2 resize-y" maxLength={512} />
        </Field>
        <Field label="Colour" hint="Used for the role chip wherever this role appears.">
          <ColourPicker value={colour} onChange={setColour} />
        </Field>
        <Note tone="info">
          The new role starts with no permissions. Grant them from the role panel after creating it.
        </Note>
      </form>
    </Modal>
  )
}

/** Edit the display fields. `name` is immutable and is not offered. */
export function EditRoleDialog({
  role, onClose, onSaved,
}: { role: RoleResponse | null; onClose: () => void; onSaved: () => void }) {
  const [displayName, setDisplayName] = useState('')
  const [description, setDescription] = useState('')
  const [colour, setColour] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!role) return
    setDisplayName(role.displayName)
    setDescription(role.description ?? '')
    setColour(role.colour ?? (COLOURS[6] as string))
    setError(null)
  }, [role])

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!role) return
    setBusy(true); setError(null)
    try {
      await updateRole(role.id, role.version, {
        displayName: displayName.trim(),
        description: description.trim(),
        colour,
      })
      onSaved()
      onClose()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={Boolean(role)}
      onClose={onClose}
      title={`Edit ${role?.displayName ?? 'role'}`}
      description={role?.system ? 'System role — only display fields can change.' : undefined}
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button type="submit" form="edit-role-form" className="erp-btn erp-btn-primary"
                  disabled={busy || !displayName.trim()}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save changes
          </button>
        </>
      }
    >
      <form id="edit-role-form" onSubmit={submit} className="space-y-4">
        {error && <Note tone="bad">{error}</Note>}
        <Field label="Name" hint="Immutable — shown for reference only.">
          <input value={role?.name ?? ''} disabled className="erp-input w-full font-mono opacity-60" />
        </Field>
        <Field label="Display name" required>
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                 className="erp-input w-full" maxLength={160} required />
        </Field>
        <Field label="Description">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                    rows={2} className="erp-input w-full !h-auto py-2 resize-y" maxLength={512} />
        </Field>
        <Field label="Colour">
          <ColourPicker value={colour} onChange={setColour} />
        </Field>
      </form>
    </Modal>
  )
}

/** Clone: copies the permission set into a new, non-system role. */
export function CloneRoleDialog({
  role, onClose, onCloned,
}: { role: RoleResponse | null; onClose: () => void; onCloned: (r: RoleResponse) => void }) {
  const [name, setName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!role) return
    setDisplayName(`${role.displayName} (copy)`)
    setName(`${role.name}_COPY`)
    setError(null)
  }, [role])

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!role) return
    setBusy(true); setError(null)
    try {
      const created = await cloneRole(role.id, { name: name.trim(), displayName: displayName.trim() })
      onCloned(created)
      onClose()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={Boolean(role)}
      onClose={onClose}
      title={`Clone ${role?.displayName ?? 'role'}`}
      description={`Copies all ${role?.permissionCount ?? 0} permissions into a new role you can then adjust.`}
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button type="submit" form="clone-role-form" className="erp-btn erp-btn-primary"
                  disabled={busy || !name.trim() || !displayName.trim()}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
            Clone role
          </button>
        </>
      }
    >
      <form id="clone-role-form" onSubmit={submit} className="space-y-4">
        {error && <Note tone="bad">{error}</Note>}
        <Field label="Display name" required>
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                 className="erp-input w-full" required />
        </Field>
        <Field label="Name" required hint="Unique and permanent.">
          <input value={name} onChange={(e) => setName(e.target.value.toUpperCase())}
                 className="erp-input w-full font-mono" pattern="[A-Z0-9_]+" required />
        </Field>
        <Note tone="info">
          The clone is always created as a normal role, never a system role — so you can
          edit and delete it freely.
        </Note>
      </form>
    </Modal>
  )
}

/**
 * Delete confirmation.
 *
 * The backend refuses to delete a system role or one still assigned to anyone,
 * so the button is pre-disabled for those cases rather than letting the user
 * discover the refusal after committing to it.
 */
export function DeleteRoleDialog({
  role, onClose, onDeleted,
}: { role: RoleResponse | null; onClose: () => void; onDeleted: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { setError(null) }, [role])

  const blocked = role?.system
    ? 'System roles cannot be deleted.'
    : (role?.memberCount ?? 0) > 0
      ? `Still assigned to ${role?.memberCount} user${role?.memberCount === 1 ? '' : 's'}. Remove those assignments first.`
      : null

  async function confirm() {
    if (!role) return
    setBusy(true); setError(null)
    try {
      await deleteRole(role.id)
      onDeleted()
      onClose()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={Boolean(role)}
      onClose={onClose}
      title={`Delete ${role?.displayName ?? 'role'}?`}
      width="sm"
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            onClick={confirm}
            disabled={busy || Boolean(blocked)}
            className={cn(
              'erp-btn',
              blocked
                ? 'opacity-40 cursor-not-allowed'
                : 'bg-red-600 text-white border-red-600 hover:bg-red-700',
            )}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            Delete role
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <Note tone="bad">{error}</Note>}
        {blocked
          ? <Note tone="info">{blocked}</Note>
          : <p className="text-erp-sm">
              This removes the role and its {role?.permissionCount ?? 0} permission grants.
              It cannot be undone.
            </p>}
      </div>
    </Modal>
  )
}
